import { apiClient } from "./apiClient";

export interface PublicOfferItem {
  id: string;
  badge: string;
  title: string;
  description?: string;
  merchant: string;
  distance?: string | null;
  distanceKm?: number | null;
  image: string;
  href: string;
  partnerSlug?: string;
  category?: string;
  serviceCategories?: string[];
}

export interface OffersResponse {
  offers: PublicOfferItem[];
  isLocationBased: boolean;
}

export const fallbackOffers: PublicOfferItem[] = [];

export async function fetchPublicOffers(params: {
  lat?: number | null;
  lng?: number | null;
  limit?: number;
  category?: string;
  search?: string;
  location?: string;
}): Promise<OffersResponse> {
  const queryParams: Record<string, string> = {
    limit: String(params.limit || 4),
  };

  if (params.category && params.category !== "All" && params.category !== "All deals") {
    queryParams.category = params.category;
  }

  if (params.search?.trim()) {
    queryParams.search = params.search.trim();
  }

  const isAll =
    !params.location ||
    params.location === "All" ||
    params.location.toLowerCase() === "all locations";

  const hasLocation =
    !isAll &&
    params.lat != null &&
    params.lng != null &&
    !isNaN(params.lat) &&
    !isNaN(params.lng);

  if (!isAll) {
    if (params.location?.trim() && params.location !== "Current Location") {
      queryParams.location = params.location.trim();
    }

    if (hasLocation) {
      queryParams.lat = String(params.lat);
      queryParams.lng = String(params.lng);
    }
  }

  try {
    const res = await apiClient.get<any>(`/loyalty/public/offers`, queryParams);

    if (res.success && res.data) {
      const items: any[] = Array.isArray(res.data)
        ? res.data
        : res.data.offers || [];

      if (items.length > 0) {
        const mappedOffers: PublicOfferItem[] = items.map((item, idx) => {
          const partner = item.partnerId || {};
          const partnerName =
            partner.businessDetails?.businessName ||
            item.businessName ||
            "Partner Store";

          const partnerSlug = partner.slug;
          const href = partnerSlug ? `/shops/${partnerSlug}` : `/deals`;

          // Image selection
          const image =
            (item.images && item.images.length > 0 && item.images[0]) ||
            partner.businessInfo?.coverImage ||
            partner.businessInfo?.businessLogo ||
            "/deals/deal_salon_cosmetics.jpg";

          // Discount Badge
          let badge = item.badge || "SPECIAL DEAL";
          if (!item.badge) {
            if (item.discountType === "percentage") {
              const val = item.discountValue || item.discountRange?.max;
              if (val) badge = `${val}% OFF`;
            } else if (item.discountType === "fixed" && item.discountValue) {
              badge = `₹${item.discountValue} OFF`;
            } else if (item.discountType === "bogo") {
              badge = "BUY 1 GET 1";
            }
          }

          // Distance
          const distance = item.distance || null;

          return {
            id: item._id || `offer-${idx}`,
            badge,
            title: item.title || "Special Offer",
            description: item.description,
            merchant: partnerName,
            distance: hasLocation ? distance : null,
            distanceKm: item.distanceKm,
            image,
            href,
            partnerSlug,
            category: item.category?.name || item.category || (partner.serviceCategories && partner.serviceCategories[0]) || "Special Offer",
            serviceCategories: partner.serviceCategories || item.serviceCategories || [],
          };
        });

        return {
          offers: mappedOffers.slice(0, params.limit || 4),
          isLocationBased: hasLocation,
        };
      }
    }
  } catch (err) {
    console.warn("Could not fetch offers from backend, using fallback:", err);
  }

  // Return empty if no offers returned from backend
  return {
    offers: [],
    isLocationBased: hasLocation,
  };
}

export interface FetchOffersDirectoryParams {
  category?: string;
  search?: string;
  location?: string;
  discountType?: string;
  sort?: "popular" | "discount" | "nearest" | "newest";
  page?: number;
  limit?: number;
  lat?: number | null;
  lng?: number | null;
}

export interface OffersDirectoryResponse {
  offers: PublicOfferItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  isLocationBased: boolean;
}

export async function fetchOffersDirectory(
  params?: FetchOffersDirectoryParams
): Promise<OffersDirectoryResponse> {
  const queryParams: Record<string, string> = {
    page: String(params?.page || 1),
    limit: String(params?.limit || 24),
  };

  if (params?.category && params.category !== "all" && params.category !== "All deals") {
    queryParams.category = params.category;
  }
  if (params?.search?.trim()) queryParams.search = params.search.trim();
  if (params?.discountType && params.discountType !== "all") {
    queryParams.discountType = params.discountType;
  }
  const isAllDir =
    !params?.location ||
    params.location === "All" ||
    params.location.toLowerCase() === "all locations";

  const hasLocation =
    !isAllDir &&
    params?.lat != null &&
    params?.lng != null &&
    !isNaN(params.lat) &&
    !isNaN(params.lng);

  if (!isAllDir) {
    if (params?.location?.trim() && params.location !== "Current Location") {
      queryParams.location = params.location.trim();
    }

    if (hasLocation) {
      queryParams.lat = String(params.lat);
      queryParams.lng = String(params.lng);
    }
    if (params?.sort) queryParams.sort = params.sort;
  } else {
    if (params?.sort && params.sort !== "nearest") {
      queryParams.sort = params.sort;
    }
  }

  try {
    const res = await apiClient.get<any>(`/loyalty/public/offers`, queryParams);
    if (res.success && res.data) {
      const items: any[] = Array.isArray(res.data)
        ? res.data
        : res.data.offers || [];

      const mappedOffers: PublicOfferItem[] = items.map((item, idx) => {
        const partner = item.partnerId || {};
        const partnerName =
          partner.businessDetails?.businessName ||
          item.businessName ||
          "Partner";

        const partnerSlug = partner.slug;
        const href = partnerSlug ? `/businesses/${partnerSlug}` : `/businesses`;

        const image =
          (item.images && item.images.length > 0 && item.images[0]) ||
          partner.businessInfo?.coverImage ||
          partner.businessInfo?.businessLogo ||
          "/deals/shop_vibe.jpg";

        let badge = item.badge || "SPECIAL DEAL";
        if (!item.badge) {
          if (item.discountType === "percentage") {
            const val = item.discountValue || item.discountRange?.max;
            if (val) badge = `${val}% OFF`;
          } else if (item.discountType === "fixed" && item.discountValue) {
            badge = `₹${item.discountValue} OFF`;
          } else if (item.discountType === "bogo") {
            badge = "BUY 1 GET 1";
          }
        }

        const distance = item.distance || null;

        return {
          id: item._id || `offer-${idx}`,
          badge,
          title: item.title || "Special Offer",
          description: item.description,
          merchant: partnerName,
          distance: hasLocation ? distance : null,
          distanceKm: item.distanceKm,
          image,
          href,
          partnerSlug,
          category: item.category?.name || item.category || (partner.serviceCategories && partner.serviceCategories[0]) || "Special Offer",
          serviceCategories: partner.serviceCategories || item.serviceCategories || [],
        };
      });

      return {
        offers: mappedOffers,
        pagination: (res as any).pagination || {
          page: 1,
          limit: mappedOffers.length,
          total: mappedOffers.length,
          pages: 1,
        },
        isLocationBased: hasLocation,
      };
    }
  } catch (err) {
    console.warn("fetchOffersDirectory error:", err);
  }

  // Return empty if no offers returned from backend
  return {
    offers: [],
    pagination: {
      page: 1,
      limit: 24,
      total: 0,
      pages: 0,
    },
    isLocationBased: false,
  };
}

export interface DetailedOffer {
  _id: string;
  title: string;
  description?: string;
  category?: string | { id?: string; name?: string };
  discountType?: string;
  discountValue?: number;
  badge?: string;
  originalPrice?: number;
  offerPrice?: number;
  images?: string[];
  validUntil?: string;
  validTo?: string;
  distance?: string;
  partnerId?: {
    _id: string;
    slug?: string;
    businessDetails?: {
      businessName?: string;
      businessType?: { id?: string; name?: string } | string;
      address?: string | { line1?: string; city?: string; district?: string };
      district?: string;
    };
    businessInfo?: {
      businessLogo?: string;
      rating?: number;
      contactPhone?: string;
    };
    serviceCategories?: string[];
  };
  termsAndConditions?: string[];
  terms?: string[];
  relatedOffers?: Array<{
    _id: string;
    title: string;
    description?: string;
    badge?: string;
    images?: string[];
  }>;
}

export async function getOfferDetails(id: string): Promise<DetailedOffer> {
  try {
    const res = await apiClient.get<DetailedOffer>(`/loyalty/public/offers/${encodeURIComponent(id)}`);
    if (res.success && res.data) {
      const data = res.data;
      const rawCat = data.category as any;
      const normalizedCategory =
        typeof rawCat === "string"
          ? rawCat
          : rawCat && typeof rawCat === "object" && rawCat.name
          ? String(rawCat.name)
          : typeof (data.partnerId?.businessDetails?.businessType) === "string"
          ? data.partnerId.businessDetails.businessType
          : (data.partnerId?.businessDetails?.businessType as any)?.name ||
            data.partnerId?.serviceCategories?.[0] ||
            "Special Offer";

      return {
        ...data,
        category: normalizedCategory,
      };
    }
  } catch (err) {
    console.warn("getOfferDetails error:", err);
  }

  throw new Error("Offer not found");
}
